import React, { useCallback, useEffect, useRef, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { apiService } from '../../services/apiService';
import { CallInsightsAnalysis } from '../adherence/types';
import type { RubricWeighting } from '../adherence/types';
import type { SalesPlaybookUsage } from './PlaybookUsagePanel';
import RubricWeightDrawer from './RubricWeightDrawer';
import SalesIntelligenceV2Section from './SalesIntelligenceV2Section';
import KnowledgeBaseSection from './KnowledgeBaseSection';
import PlaybookSection from './PlaybookSection';
import CallSummaryTab from './summary/CallSummaryTab';
import RubricSection from './RubricSection';
import SupportTicketModal from '../SupportTicketModal';

interface SpeechRateAlert {
  total_words: number;
  words_per_minute: number;
  timestamp: string;
}

interface CallInsightsTabProps {
  sessionId: string;
  callInsights?: CallInsightsAnalysis;
  salesPlaybookUsage?: SalesPlaybookUsage;
  speechRateAlerts: SpeechRateAlert[];
  isAdmin: boolean;
  onAnalysisUpdated: () => void;
  onNavigateToTimestamp?: (ts: string) => void;
  // Historical-upload-only: true when two speakers were tagged "salesperson"
  // (team-selling call) — forwarded to RubricSection for the score badge.
  multipleSalespeople?: boolean;
  // This call's saved rubric weighting, if an admin has retuned it.
  rubricWeighting?: RubricWeighting;
}

const TOTAL_DIMENSIONS = 6;
const MAX_POLL_ATTEMPTS = 100;

const DIMENSION_LABELS: Record<string, string> = {
  objection_faq: 'Objections / FAQs',
  landmine: 'Landmines',
  competitor: 'Competitors',
  playbook: 'Playbook',
  sales_intelligence: 'Sales Intelligence',
  tone_analysis: 'Tone Analysis',
};

function formatGeneratedAt(dateStr?: string): string {
  if (!dateStr) return '';
  try {
    return format(parseISO(dateStr), 'MMM d, yyyy h:mm a');
  } catch {
    return dateStr;
  }
}

interface SectionAccordionProps {
  id: string;
  label: string;
  headerColor: string;
  borderColor: string;
  isExpanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

const SectionAccordion: React.FC<SectionAccordionProps> = ({
  label,
  headerColor,
  borderColor,
  isExpanded,
  onToggle,
  children,
}) => (
  <div className={`border-l-4 ${borderColor} bg-white rounded-xl overflow-hidden shadow-sm border border-gray-200`}>
    <button
      type="button"
      onClick={onToggle}
      className={`w-full flex items-center justify-between px-5 py-4 ${headerColor} transition-colors text-left`}
    >
      <span className="text-sm font-bold uppercase tracking-wider">{label}</span>
      <span
        className="text-xs transition-transform duration-200 flex-shrink-0"
        style={{ transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)', display: 'inline-block' }}
      >
        &#9660;
      </span>
    </button>
    {isExpanded && (
      <div className="border-t border-gray-100">
        {children}
      </div>
    )}
  </div>
);

const CallInsightsTab: React.FC<CallInsightsTabProps> = ({
  sessionId,
  callInsights,
  salesPlaybookUsage,
  speechRateAlerts,
  isAdmin,
  onAnalysisUpdated,
  onNavigateToTimestamp,
  multipleSalespeople,
  rubricWeighting,
}) => {
  const [isReanalyzing, setIsReanalyzing] = useState(false);
  const [showWeightDrawer, setShowWeightDrawer] = useState(false);
  const [defaultInstruction, setDefaultInstruction] = useState('');
  const [reanalyzeError, setReanalyzeError] = useState<string | null>(null);
  const [activeSubTab, setActiveSubTab] = useState<'summary' | 'details'>('summary');
  const [expandedSection, setExpandedSection] = useState<string | null>('sales_intelligence');
  const [detailsInitialSubSection, setDetailsInitialSubSection] = useState<string | undefined>(undefined);
  const pollCountRef = useRef(0);

  const handleSwitchToDetails = useCallback(
    (section: 'sales_intelligence' | 'knowledge_base' | 'playbook', subSection?: string) => {
      setExpandedSection(section);
      setDetailsInitialSubSection(subSection);
      setActiveSubTab('details');
    },
    []
  );

  const toggleSection = useCallback((id: string) => {
    setExpandedSection((prev) => (prev === id ? null : id));
  }, []);

  // Auto-poll while pending
  const isPending = callInsights?.status === 'pending';
  // Distinguishes an admin weight change from a full re-analysis, so the
  // loading copy explains what is actually happening.
  const isReweighting = isPending && callInsights?.triggered_by === 'reweight';
  useEffect(() => {
    if (!isPending) {
      pollCountRef.current = 0;
      return;
    }
    const intervalId = setInterval(() => {
      if (pollCountRef.current >= MAX_POLL_ATTEMPTS) {
        clearInterval(intervalId);
        return;
      }
      pollCountRef.current += 1;
      onAnalysisUpdated();
    }, 6000);
    return () => clearInterval(intervalId);
  }, [isPending, onAnalysisUpdated]);

  const handleReanalyze = async () => {
    setIsReanalyzing(true);
    setReanalyzeError(null);
    try {
      const response = await apiService.post(
        `/admin/sessions/${sessionId}/call-insights-reanalyze`
      );
      if (!response.success) {
        throw new Error(response.error ?? 'Failed to start re-analysis');
      }
      onAnalysisUpdated();
    } catch (err: any) {
      setReanalyzeError(err?.message ?? 'An unexpected error occurred.');
    } finally {
      setIsReanalyzing(false);
    }
  };

  // Tenant-wide default scoring instruction, offered as a starting point when
  // this call has none of its own. Non-fatal if it fails — the drawer just
  // won't show the "use organization default" shortcut.
  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    apiService
      .get('/settings/rubric-defaults')
      .then((response) => {
        if (!cancelled && response?.success) {
          setDefaultInstruction(response.data?.defaultScoringInstruction ?? '');
        }
      })
      .catch(() => {
        /* optional convenience only — the editor works without it */
      });
    return () => {
      cancelled = true;
    };
  }, [isAdmin]);

  const handleSaveWeights = useCallback(
    async (payload: { weights: Record<string, number>; customInstruction: string; lockedKeys: string[] }) => {
      const response = await apiService.put(
        `/admin/sessions/${sessionId}/rubric-weighting`,
        payload
      );
      if (!response?.success) {
        throw new Error(response?.message ?? 'Failed to save weights');
      }
      onAnalysisUpdated();
    },
    [sessionId, onAnalysisUpdated]
  );

  // --- Support ticket (reuses the /support-tickets create modal) ---
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [supportSubmitting, setSupportSubmitting] = useState(false);
  const [supportError, setSupportError] = useState<string | null>(null);

  const handleSupportSubmit = async (data: {
    title: string;
    description: string;
    severity: 'low' | 'medium' | 'high';
    files: File[];
  }) => {
    setSupportSubmitting(true);
    setSupportError(null);
    try {
      const formData = new FormData();
      formData.append('title', data.title);
      formData.append('description', data.description);
      formData.append('severity', data.severity);
      data.files.forEach((file) => formData.append('attachments', file));

      const response = await apiService.postFormData('/admin/support-tickets', formData);
      if (!response?.success) {
        throw new Error(response?.error ?? 'Failed to submit support ticket');
      }
      setShowSupportModal(false);
    } catch (err: any) {
      setSupportError(err?.message ?? 'An unexpected error occurred.');
    } finally {
      setSupportSubmitting(false);
    }
  };

  // Prefill the support ticket with the failing-analysis context for this session.
  const failedRubricCategories = (callInsights?.rubricAnalysis?.categories ?? []).filter(
    (c: any) => c?.raw_score == null || !!c?.error
  );
  const supportPrefill = {
    title: `Call Insights analysis incomplete — session ${sessionId}`,
    description:
      `The Discovery Call Rubric analysis for session ${sessionId} could not grade all ` +
      `dimensions.\n\nFailed dimensions (${failedRubricCategories.length}): ` +
      `${failedRubricCategories.map((c: any) => c?.title || c?.key).join(', ') || 'n/a'}.\n\n` +
      `Please look into why the analysis is failing.`,
    severity: 'medium' as const,
  };

  const completedDimensions = callInsights?.dimensions_complete ?? [];

  // Lushin runs the discovery rubric (10 categories) instead of the 6 legacy
  // dimensions. Detect rubric mode by the presence of rubricAnalysis (complete)
  // or by progress markers that aren't legacy dimension keys (pending).
  const isRubricMode =
    !!callInsights?.rubricAnalysis ||
    completedDimensions.some((d) => !(d in DIMENSION_LABELS));
  const totalDimensions = isRubricMode ? 10 : TOTAL_DIMENSIONS;

  // --- Empty state ---
  if (!callInsights) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <p className="text-lg font-semibold text-gray-600 mb-2">No Analysis Yet</p>
        <p className="text-sm text-gray-400 max-w-sm mb-5">
          Run a Call Insights analysis to get AI-powered insights on knowledge base handling,
          playbook adherence, and sales intelligence.
        </p>
        {isAdmin && (
          <button
            onClick={handleReanalyze}
            disabled={isReanalyzing}
            className="bg-blue-600 text-white px-5 py-2 rounded font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
            type="button"
          >
            {isReanalyzing ? 'Starting Analysis...' : 'Run Analysis'}
          </button>
        )}
        {reanalyzeError && (
          <p className="mt-3 text-sm text-red-600">{reanalyzeError}</p>
        )}
      </div>
    );
  }

  // --- Failed state ---
  if (callInsights.status === 'failed') {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <p className="text-lg font-semibold text-red-600 mb-2">Analysis Failed</p>
        {callInsights.error && (
          <p className="text-sm text-red-500 bg-red-50 border border-red-200 rounded px-4 py-2 max-w-md mb-4">
            {callInsights.error}
          </p>
        )}
        {isAdmin && (
          <button
            onClick={handleReanalyze}
            disabled={isReanalyzing}
            className="bg-blue-600 text-white px-5 py-2 rounded font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
            type="button"
          >
            {isReanalyzing ? 'Starting...' : 'Retry Analysis'}
          </button>
        )}
        {reanalyzeError && (
          <p className="mt-3 text-sm text-red-600">{reanalyzeError}</p>
        )}
      </div>
    );
  }

  // --- Pending / Complete state ---
  const completedCount = completedDimensions.length;
  const progressPct = Math.min(100, Math.round((completedCount / totalDimensions) * 100));

  return (
    <div className="space-y-3">
      {/* Top meta bar */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          {callInsights.generated_at && callInsights.status === 'complete' && (
            <span className="text-xs text-gray-400">
              Generated: {formatGeneratedAt(callInsights.generated_at)}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {reanalyzeError && (
            <span className="text-xs text-red-600">{reanalyzeError}</span>
          )}
          {isAdmin && callInsights.status === 'complete' && (
            <button
              onClick={handleReanalyze}
              disabled={isReanalyzing}
              className="text-xs bg-white text-gray-600 border border-gray-300 px-3 py-1.5 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
              type="button"
            >
              {isReanalyzing ? 'Re-analyzing...' : 'Re-analyze'}
            </button>
          )}
        </div>
      </div>

      {/* Progress bar when pending */}
      {isPending && (
        <div className="flex flex-col items-center py-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
            <p className="text-sm font-semibold text-gray-700">
              {isReweighting ? 'Re-scoring with your adjusted weights...' : 'Analysis in progress...'}
            </p>
          </div>
          {isReweighting && (
            <p className="text-xs text-gray-500 max-w-sm text-center mb-3">
              Only the dimensions affected by your change are being re-graded. The coach&rsquo;s
              read is rewritten for the new weighting.
            </p>
          )}
          {completedCount > 0 ? (
            <div className="w-full max-w-xs">
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>
                  {completedCount} of {totalDimensions} {isRubricMode ? 'categories' : 'dimensions'} complete
                </span>
                <span>{progressPct}%</span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-500 rounded-full transition-all duration-500"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <ul className="mt-3 space-y-1 text-left">
                {completedDimensions.map((dim) => (
                  <li key={dim} className="flex items-center gap-1.5 text-xs text-gray-600">
                    <span className="text-green-500 font-bold">&#10003;</span>
                    {DIMENSION_LABELS[dim] ?? dim}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            !isReweighting && (
              <p className="text-sm text-gray-400 max-w-sm text-center">
                The AI is analyzing the session according the grading rubrik. This may take a minute or two.
              </p>
            )
          )}
        </div>
      )}

      {/* Lushin discovery rubric — replaces the 6-dimension view when present. */}
      {callInsights.status === 'complete' && isRubricMode && (
        <RubricSection
          rubricAnalysis={callInsights.rubricAnalysis}
          onNavigateToTimestamp={onNavigateToTimestamp}
          isAdmin={isAdmin}
          onReanalyze={handleReanalyze}
          isReanalyzing={isReanalyzing}
          onContactSupport={() => setShowSupportModal(true)}
          multipleSalespeople={multipleSalespeople}
          onAdjustWeights={() => setShowWeightDrawer(true)}
        />
      )}

      {isAdmin && isRubricMode && (
        <RubricWeightDrawer
          open={showWeightDrawer}
          onClose={() => setShowWeightDrawer(false)}
          categories={callInsights.rubricAnalysis?.categories ?? []}
          initialWeights={rubricWeighting?.weights}
          initialInstruction={rubricWeighting?.customInstruction}
          initialLockedKeys={rubricWeighting?.lockedKeys}
          defaultInstruction={defaultInstruction}
          onSave={handleSaveWeights}
        />
      )}

      {/* Sub-tab toggle + content — legacy 6-dimension view (live-intelligence). */}
      {callInsights.status === 'complete' && !isRubricMode && (
        <div className="space-y-4">
          {/* Sub-tab navigation */}
          <div className="flex border-b border-gray-200">
            <button
              type="button"
              onClick={() => setActiveSubTab('summary')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeSubTab === 'summary'
                  ? 'border-blue-600 text-blue-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Summary
            </button>
            <button
              type="button"
              onClick={() => setActiveSubTab('details')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeSubTab === 'details'
                  ? 'border-blue-600 text-blue-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Details
            </button>
          </div>

          {/* Summary sub-tab */}
          {activeSubTab === 'summary' && (
            <CallSummaryTab
              sessionId={sessionId}
              callInsights={callInsights}
              salesPlaybookUsage={salesPlaybookUsage}
              speechRateAlerts={speechRateAlerts}
              isAdmin={isAdmin}
              onAnalysisUpdated={onAnalysisUpdated}
              onNavigateToTimestamp={onNavigateToTimestamp}
              onSwitchToDetails={handleSwitchToDetails}
            />
          )}

          {/* Details sub-tab */}
          {activeSubTab === 'details' && (
            <div className="space-y-4">
              <SectionAccordion
                id="sales_intelligence"
                label="Sales Intelligence"
                headerColor="bg-blue-50 hover:bg-blue-100 text-blue-900"
                borderColor="border-l-blue-500"
                isExpanded={expandedSection === 'sales_intelligence'}
                onToggle={() => toggleSection('sales_intelligence')}
              >
                <div className="p-5">
                  <SalesIntelligenceV2Section
                    sessionId={sessionId}
                    intelligence={callInsights?.sales_intelligence!}
                    callInsights={callInsights}
                    speechRateAlerts={speechRateAlerts}
                    isAdmin={isAdmin}
                    onReviewSaved={onAnalysisUpdated}
                    onNavigateToTimestamp={onNavigateToTimestamp}
                  />
                </div>
              </SectionAccordion>

              <SectionAccordion
                id="knowledge_base"
                label="Knowledge Base Handling"
                headerColor="bg-amber-50 hover:bg-amber-100 text-amber-900"
                borderColor="border-l-amber-500"
                isExpanded={expandedSection === 'knowledge_base'}
                onToggle={() => toggleSection('knowledge_base')}
              >
                <div className="p-5">
                  <KnowledgeBaseSection
                    sessionId={sessionId}
                    callInsights={callInsights}
                    isAdmin={isAdmin}
                    onReviewSaved={onAnalysisUpdated}
                    onNavigateToTimestamp={onNavigateToTimestamp}
                    initialExpandedAccordion={detailsInitialSubSection}
                  />
                </div>
              </SectionAccordion>

              <SectionAccordion
                id="playbook"
                label="Playbook"
                headerColor="bg-green-50 hover:bg-green-100 text-green-900"
                borderColor="border-l-green-500"
                isExpanded={expandedSection === 'playbook'}
                onToggle={() => toggleSection('playbook')}
              >
                <div className="p-5">
                  <PlaybookSection
                    sessionId={sessionId}
                    callInsights={callInsights}
                    salesPlaybookUsage={salesPlaybookUsage}
                    isAdmin={isAdmin}
                    onReviewSaved={onAnalysisUpdated}
                  />
                </div>
              </SectionAccordion>
            </div>
          )}
        </div>
      )}

      {/* Support ticket modal (reused from the /support-tickets page), opened
          from the incomplete-analysis error banner in RubricSection. */}
      <SupportTicketModal
        isOpen={showSupportModal}
        onClose={() => setShowSupportModal(false)}
        onSubmit={handleSupportSubmit}
        submitting={supportSubmitting}
        error={supportError}
        initialValues={supportPrefill}
      />
    </div>
  );
};

export default CallInsightsTab;
